import React from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed, autorun } from 'mobx';
import _ from 'lodash';
import moment from 'moment';
import { SelectInput, TextInput } from './Form';

@observer
class CronIntervalInput extends React.Component {

  intervalOptions = [
    'Minutes',
    'Hourly',
    'Daily',
    'Monthly',
  ];

  @observable duration = 'Hourly';
  @observable openSettings = true;
  @observable minuteEvery = 1;
  @observable hourlyOptions = {
    every: {
      value: 1
    },
    at: {
      value: 0
    }
  };
  @observable monthlyOptions = {
    day: {
      value: 1
    },
    hour: {
      value: 0
    }
  };

  @observable dailyOptions = {
    every: {
      options: [
        { label: 'Day', value: '*' },
        { label: 'Sunday', value: '0' },
        { label: 'Monday', value: '1' },
        { label: 'Tuesday', value: '2' },
        { label: 'Wednesday', value: '3' },
        { label: 'Thursday', value: '4' },
        { label: 'Friday', value: '5' },
        { label: 'Saturday', value: '6' },
      ],
      value: '*'
    },
    at: {
      value: '00:00'
    }
  };

  @computed get cronFormat() {
    if (this.duration === 'Minutes') {
      return `*/${this.minuteEvery} * * * *`
    } else if (this.duration === 'Hourly') {
      let { every, at } = this.hourlyOptions;
      return `${at.value} */${every.value} * * *`;
    } else if (this.duration === 'Daily') {
      let { every, at } = this.dailyOptions;
      let hour = +at.value.split(':')[0],
        minute = +at.value.split(':')[1];
      return `${minute} ${hour} * * ${every.value}`
    } else if (this.duration === 'Monthly') {
      let { day, hour } = this.monthlyOptions;
      return `0 ${hour.value} ${day.value} */1 *`;
    }
  }

  dispose;
  componentWillMount() {
    const { onChange, value } = this.props;
    if (!!value && value !== '') this.formatDecode(value);
    this.dispose = autorun(() => {
      onChange(_.set({}, 'target.value', this.cronFormat));
    })
  }

  componentWillUnmount() {
    this.dispose();
  }

  formatDecode = cronFormat => {
    cronFormat = cronFormat.split(' ');
    if (cronFormat.length === 1) return;

    let minute = cronFormat[0],
      hour = cronFormat[1],
      day = cronFormat[2],
      month = cronFormat[3],
      weekDay = cronFormat[4];

    if (minute.indexOf('/') > -1 && this.isAsterisk([hour, day, month, weekDay])) {
      this.duration = 'Minutes';
      this.minuteEvery = +minute.split('/')[1];
    } else if (this.isNumber([minute]) && !this.isNumber([hour]) && hour.indexOf('*') > -1 && this.isAsterisk([day, month, weekDay])) {
      this.duration = 'Hourly';
      this.hourlyOptions.every.value = hour.split('/')[1];
      this.hourlyOptions.at.value = minute;
    } else if (this.isNumber([minute, hour]) && this.isAsterisk([day, month]) && (this.isAsterisk([weekDay]) || this.isNumber([weekDay]))) {
      this.duration = 'Daily';
      this.dailyOptions.every.value = weekDay;
      this.dailyOptions.at.value = moment().hours(hour).minutes(minute).format('HH:mm');
    } else if (month === '*/1') {
      this.duration = 'Monthly';
      this.monthlyOptions.day.value = day;
      this.monthlyOptions.hour.value = hour;
    }
  };

  isAsterisk = arr => arr.reduce((acc, item) => (acc && item === '*'), true);
  isNumber = arr => arr.reduce((acc, item) => (acc && (!!+item || +item === 0)), true);

  @action onChangeDuration = value => this.duration = value;
  @action handleOpenSettings = () => this.openSettings = !this.openSettings;
  @action onChangeMinuteEvery = e => this.minuteEvery = e.target.value;
  @action onChangeHourlyOptionValue = (option, e) => this.hourlyOptions[option].value = e.target.value;
  @action onChangeDailyOptionValue = (option, e) => this.dailyOptions[option].value = e.target.value;
  @action onChangeMonthlyOptionValue = (option, e) => this.monthlyOptions[option].value = e.target.value;

  _renderMinuteOptions = () => {
    return (
      <div className='cronInput'>
        <label>Every</label>
        <TextInput small type='number' value={this.minuteEvery} onChange={this.onChangeMinuteEvery} inputProps={{ min: 1, max: 59, step: 1 }} />
        <span>minute(s)</span>
      </div>
    )
  };

  _renderHourlyOptions = () => {
    return (
      <div>
        <div className='cronInput'>
          <label>Every</label>
          <TextInput small type='number' value={this.hourlyOptions.every.value} onChange={this.onChangeHourlyOptionValue.bind(this, 'every')} inputProps={{ min: 1, max: 12, step: 1 }} />
          <span>hour(s)</span>
        </div>
        <div className='cronInput'>
          <label>At Minute</label>
          <TextInput small type='number' value={this.hourlyOptions.at.value} onChange={this.onChangeHourlyOptionValue.bind(this, 'at')} inputProps={{ min: 0, max: 59, step: 1 }} />
        </div>
      </div>
    )
  };


  _renderDailyOptions = () => {
    return (
      <div>
        <div className='cronInput'>
          <label>Every</label>
          <SelectInput id='cron-interval-daily' label='' value={this.dailyOptions.every.value} onChange={this.onChangeDailyOptionValue.bind(this, 'every')} options={this.dailyOptions.every.options} fields={{ label: 'label', value: 'value' }} small />
        </div>
        <div className='cronInput'>
          <label>Starting Time</label>
          <TextInput small type='time' value={this.dailyOptions.at.value} onChange={this.onChangeDailyOptionValue.bind(this, 'at')} />
        </div>
      </div>
    )
  };

  _renderMonthlyOptions = () => {
    return (
      <div>
        <div className='cronInput' >
          <label>At Day of Month</label>
          <TextInput small type='number' value={this.monthlyOptions.day.value} onChange={this.onChangeMonthlyOptionValue.bind(this, 'day')} inputProps={{ min: 1, max: 20, step: 1 }} />
        </div>
        <div className='cronInput' >
          <label>At Hour</label>
          <TextInput small type='number' value={this.monthlyOptions.hour.value} onChange={this.onChangeMonthlyOptionValue.bind(this, 'hour')} inputProps={{ min: 0, max: 23, step: 1 }} />
        </div>
      </div>
    )
  };


  render() {
    const { label } = this.props;
    return (
      <div className='formInput flex flex-row'>
        <label>{label}</label>
        <div className='bordered overflow-x-hidden'>
          <div className='TabBar'>
            <ul>
              {this.intervalOptions.map(item => <li key={`interval-duration-${item}`} className={this.duration === item ? 'active' : ''} onClick={() => this.onChangeDuration(item)}><a>{item}</a></li>)}
            </ul>
          </div>
          <div className='overflow-x-scroll' style={{ padding: '10px 20px' }}>
            {this.duration === 'Minutes' && this._renderMinuteOptions()}
            {this.duration === 'Hourly' && this._renderHourlyOptions()}
            {this.duration === 'Daily' && this._renderDailyOptions()}
            {this.duration === 'Monthly' && this._renderMonthlyOptions()}
          </div>
        </div>
      </div>
    );
  }
}

export default CronIntervalInput;


// WEBPACK FOOTER //
// ./src/components/CronIntervalInput.js